/**
 * Authentication Provider for Report Summarizer
 * Provides authentication context and manages auth state
 */

import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { useAuth as useOidcAuth } from 'oidc-react'
import { AuthState, AuthContextValue, User } from './types'
import { 
  storeTokens, 
  clearTokens, 
  getAccessToken, 
  getWebSessionToken, 
  getUserFromToken,
  isTokenExpired 
} from './tokenManager'

const AuthContext = createContext<AuthContextValue | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

/**
 * Authentication provider that wraps OIDC functionality
 */
export function AuthProvider({ children }: AuthProviderProps) {
  const oidcAuth = useOidcAuth()
  const [authState, setAuthState] = useState<AuthState>({
    isAuthenticated: false,
    isLoading: true,
    user: null,
    error: null,
  })

  useEffect(() => {
    const initializeAuth = async () => {
      try {
        // Check if we have valid tokens
        const accessToken = getAccessToken()
        
        if (accessToken && !isTokenExpired()) {
          // We have a valid token, extract user info
          const user = getUserFromToken(accessToken)
          if (user) {
            setAuthState({
              isAuthenticated: true,
              isLoading: false,
              user,
              error: null,
            })
            return
          }
        }

        // Check OIDC auth state
        if (oidcAuth.userData) {
          const user: User = {
            id: oidcAuth.userData.profile.sub,
            email: oidcAuth.userData.profile.email || '',
            firstName: oidcAuth.userData.profile.given_name,
            lastName: oidcAuth.userData.profile.family_name,
            name: oidcAuth.userData.profile.name,
            sub: oidcAuth.userData.profile.sub,
            preferred_username: oidcAuth.userData.profile.preferred_username,
            given_name: oidcAuth.userData.profile.given_name,
            family_name: oidcAuth.userData.profile.family_name,
          }

          // Store tokens
          if (oidcAuth.userData.access_token) {
            storeTokens(oidcAuth.userData.access_token)
          }

          setAuthState({
            isAuthenticated: true,
            isLoading: false,
            user,
            error: null,
          })
        } else {
          setAuthState({
            isAuthenticated: false,
            isLoading: false,
            user: null,
            error: null,
          })
        }
      } catch (error) {
        console.error('Auth initialization error:', error)
        setAuthState({
          isAuthenticated: false,
          isLoading: false,
          user: null,
          error: error instanceof Error ? error.message : 'Authentication error',
        })
      }
    }

    initializeAuth()
  }, [oidcAuth.userData, oidcAuth.isLoading])

  const login = () => {
    oidcAuth.signIn()
  }

  const logout = () => {
    clearTokens()
    oidcAuth.signOut()
    setAuthState({
      isAuthenticated: false,
      isLoading: false,
      user: null,
      error: null,
    })
  }

  const contextValue: AuthContextValue = {
    ...authState,
    login,
    logout,
    getAccessToken,
    getWebSessionToken,
  }

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  )
}

/**
 * Hook to access authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}